import React, { useCallback, useEffect, useRef, useState, useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { getDocument } from '@atlaskit/browser-apis';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts/ui/Spotlight';
import { SpotlightManager } from '@atlassiansox/engagekit-ts/spotlight-manager';
import { SpotlightTarget as AkSpotlightTarget } from '@atlassiansox/engagekit-ts/spotlight-target';
import { MenuIdHelp } from '@jira/navigation-apps__constants/src/MenuIdHelp.tsx';
import { Help as Trigger } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/help-button/index.tsx';
import {
	NAVIGATION_ITEM_ID,
	HELP_SPOTLIGHT_KEY,
	HELP_SPOTLIGHT_EVENT_NAME,
} from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { TopLevelErrorBoundary } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/error-boundary/index.tsx';
import { ARTICLE_TYPE } from '@atlaskit/help/model/Help';
import { useIsSimplifiedProject } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { usePrevious } from '@jira/platform__utils-react-hooks-use-previous/src/common/utils/index.tsx';
import { testIdConcat, testIdGenerate } from '@jira/navigation-apps__common/src/utils/test-id.tsx';
import {
	useRightSidebarController,
	useActiveRightSidebarState as useRightSidebarState,
} from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useTopMenus } from '@jira/navigation-apps__common/src/controllers/top-menus/index.tsx';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
	HELP_PANEL_VERSION,
} from '@jira/platform__help-panel-common-constants/src/index.tsx';
import { useHelpPanelActions } from '@jira/platform__services-help-panel/src/components/index.tsx';
import { usePanelManager } from '@atlassian/panel-system';
import { fg } from '@atlassian/jira-feature-gating';
import { useHelpButtonState } from '../../controllers/help-button/index.tsx';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import { HelpSpotlightPopup } from './HelpSpotlightPopup.tsx';
import { Badge } from './badge/index.tsx';
import type { HelpButton_query$key } from './__generated__/HelpButton_query.graphql.ts';

const tooltip = defineMessage({
	defaultMessage: 'Help',
	id: 'atlassian-navigation.help.tooltip',
	description:
		'Tooltip text displayed when hovering over the Help button in the top navigation bar',
});

const label = defineMessage({
	defaultMessage: 'Help',
	id: 'atlassian-navigation.help.label',
	description:
		'Accessible label for the Help button in the top navigation bar that opens the help dropdown menu',
});

interface SpotlightTargetProps {
	/**
	 * A single child.
	 */
	children: ReactNode;
	/**
	 * The name to reference from Spotlight.
	 */
	name: string;
}

type HelpSpotlightEventDetail = {
	show: boolean;
	bodyText?: string;
	headingText?: string;
	dismissLabel?: string;
	primaryActionLabel?: string;
};

export function SpotlightTargetComponent({ children }: { children: ReactNode }): React.JSX.Element {
	return (
		<SpotlightManager>
			<SpotlightTarget name={HELP_SPOTLIGHT_KEY}>{children}</SpotlightTarget>
			<EngagementSpotlight isMigrated name={HELP_SPOTLIGHT_KEY} />
		</SpotlightManager>
	);
}

function SpotlightTarget({ children, ...props }: SpotlightTargetProps) {
	return (
		<AkSpotlightTarget {...props}>
			{/*
			 * The atlaskit spotlight target component merely registers a ref on the child component,
			 *  so there is no way to give it a test id. We want to keep the test id as closely coupled
			 *  with the target as possible, so we add a span with id here, rather than on the child
			 *  where it could become more easily uncoupled.
			 */}
			<div data-testid={`ak-spotlight-target-${props.name}`}>{children}</div>
		</AkSpotlightTarget>
	);
}

const getReleaseNotesNotifications: (jsonValue: string | null | undefined) => number | null = (
	jsonValue,
) => {
	if (!jsonValue) {
		return null;
	}
	try {
		const parsed = JSON.parse(jsonValue);
		return parsed?.helpPanelMenu?.releaseNotesNotifications ?? null;
	} catch {
		return null;
	}
};

export type HelpButtonProps = {
	QueryFragment: HelpButton_query$key;
};

type HelpButtonCoreProps = {
	relayInitialValue: number | null | undefined;
};

const HelpButtonCore = ({ relayInitialValue }: HelpButtonCoreProps) => {
	const { releaseNotesNotifications, setReleaseNotesNotifications } =
		useHelpButtonState(relayInitialValue);

	const isProjectSimplified = useIsSimplifiedProject();
	const { formatMessage } = useIntl();
	const [isMenuOpen, { off: closeMenu, on: openMenu }] = useTopMenus(MenuIdHelp);
	const isAnonymous = useIsAnonymous();
	const [topMenusValue, { off: closeAllMenus }] = useTopMenus();
	const [, { setNavigationData }] = useHelpPanelActions();

	const helpButtonRef = useRef<HTMLButtonElement>(null);

	// Client-driven spotlight nudge: a product surface (e.g. JSM Rovo Service
	// onboarding) can dispatch a DOM event to light up this global Help button
	// and point the user at where they can bring the dismissed content back from.
	// Fully gated so every other product renders identically to before.
	const [spotlightDetail, setSpotlightDetail] = useState<HelpSpotlightEventDetail | null>(null);

	useEffect(() => {
		if (!fg('esa_onboarding_carousel_enabled')) {
			return undefined;
		}
		const doc = getDocument();
		if (!doc) {
			return undefined;
		}
		const isHelpSpotlightEvent = (event: Event): event is CustomEvent<HelpSpotlightEventDetail> =>
			event instanceof CustomEvent;
		const handleSpotlightEvent = (event: Event) => {
			if (!isHelpSpotlightEvent(event)) {
				return;
			}
			const { detail } = event;
			setSpotlightDetail(detail?.show ? detail : null);
		};
		doc.addEventListener(HELP_SPOTLIGHT_EVENT_NAME, handleSpotlightEvent);
		return () => doc.removeEventListener(HELP_SPOTLIGHT_EVENT_NAME, handleSpotlightEvent);
		// Feature gates are static for the session, so this effect only needs to run once.
	}, []);

	const dismissSpotlight = useCallback(() => {
		setSpotlightDetail(null);
	}, []);

	const attributes = useMemo(
		() => ({
			helpPanelVersion: HELP_PANEL_VERSION,
			helpPanelType: 'inProductHelp',
			nextGenProject: isProjectSimplified,
		}),
		[isProjectSimplified],
	);

	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.HELP,
	});

	const { openRightSidebar, closeCurrentRightSidebar } = useRightSidebarController();
	const currentRightSidebarState = useRightSidebarState();
	const isHelpPanelOpenedPreviously = usePrevious(currentRightSidebarState);
	const testIdPrefix = testIdGenerate('secondary-actions', 'help');
	const { state: panelState, manager } = usePanelManager();

	const onClick = useCallback(() => {
		if (fg('ads-panel-system-jira-admin')) {
			if (panelState.activePanels[0]) {
				const currentPanelInstanceId = panelState.activePanels[0].instanceId;
				manager.closePanel(currentPanelInstanceId);
			}
		}

		if (
			currentRightSidebarState &&
			currentRightSidebarState.panelId === HELP_PANEL_ID &&
			!currentRightSidebarState.isCollapsed
		) {
			closeCurrentRightSidebar();
		} else {
			openRightSidebar(HELP_PANEL_ID, HELP_PANEL_WIDTH, false, true);
		}

		closeAllMenus();
		setNavigationData({ articleId: { id: '', type: ARTICLE_TYPE.HELP_ARTICLE }, history: [] });

		// Shows if the help panel is currently open or closed
		const isPanelOpen =
			currentRightSidebarState?.panelId === HELP_PANEL_ID && !currentRightSidebarState?.isCollapsed;

		triggerAnalytics({
			...attributes,
			isOpened: !isPanelOpen,
		});
	}, [
		currentRightSidebarState,
		closeAllMenus,
		setNavigationData,
		triggerAnalytics,
		attributes,
		panelState.activePanels,
		manager,
		closeCurrentRightSidebar,
		openRightSidebar,
	]);

	const badge = useCallback(() => {
		if (isAnonymous) {
			return null;
		}

		return <Badge releaseNotesNotifications={releaseNotesNotifications ?? 0} />;
	}, [isAnonymous, releaseNotesNotifications]);

	// Mirror the same logic as the original Help component: when the help panel is open and
	// there are unread notifications, tell the hook to clear them. The hook handles the REST PUT.
	const shouldRestoreNotificationsCount =
		currentRightSidebarState?.isCollapsed === false && releaseNotesNotifications !== 0;

	useEffect(() => {
		if (shouldRestoreNotificationsCount) {
			setReleaseNotesNotifications(0);
		}
	}, [setReleaseNotesNotifications, shouldRestoreNotificationsCount]);

	useEffect(() => {
		if (
			currentRightSidebarState?.panelId === HELP_PANEL_ID &&
			!currentRightSidebarState?.isCollapsed
		) {
			openMenu();
		} else {
			closeMenu();
			if (isHelpPanelOpenedPreviously?.panelId === HELP_PANEL_ID && !currentRightSidebarState) {
				requestAnimationFrame(() => helpButtonRef.current?.focus());
			}
		}
	}, [closeMenu, currentRightSidebarState, isHelpPanelOpenedPreviously, openMenu, topMenusValue]);

	const triggerContent = (
		<SpotlightTargetComponent>
			<Trigger
				aria-expanded={false}
				aria-haspopup={false}
				isSelected={isMenuOpen}
				badge={badge}
				onClick={onClick}
				tooltip={formatMessage(tooltip)}
				label={formatMessage(label)}
				ref={helpButtonRef}
			/>
		</SpotlightTargetComponent>
	);

	return (
		<div
			data-testid={testIdConcat(testIdPrefix, 'menu-trigger')}
			data-vc="atlassian-navigation-help"
		>
			{spotlightDetail && fg('esa_onboarding_carousel_enabled') ? (
				<HelpSpotlightPopup
					isOpen
					onDismiss={dismissSpotlight}
					bodyText={spotlightDetail.bodyText ?? ''}
					headingText={spotlightDetail.headingText ?? ''}
					dismissLabel={spotlightDetail.dismissLabel ?? ''}
					primaryActionLabel={spotlightDetail.primaryActionLabel ?? ''}
				>
					{triggerContent}
				</HelpSpotlightPopup>
			) : (
				triggerContent
			)}
		</div>
	);
};

const HelpButtonAnonymous = () => <HelpButtonCore relayInitialValue={undefined} />;

const HelpButtonLoggedIn = ({ QueryFragment }: HelpButtonProps) => {
	const queryData = useFragment<HelpButton_query$key>(
		graphql`
			fragment HelpButton_query on Query {
				jira {
					jsonUserProperty(cloudId: $cloudId, propertyKey: "help_button_ui_state") {
						jsonValue
					}
				}
			}
		`,
		QueryFragment,
	);
	return (
		<HelpButtonCore
			relayInitialValue={getReleaseNotesNotifications(queryData?.jira?.jsonUserProperty?.jsonValue)}
		/>
	);
};

export type HelpButtonOuterProps = {
	QueryFragment: HelpButton_query$key | null;
};

function HelpButton({ QueryFragment }: HelpButtonOuterProps): React.JSX.Element {
	const isAnonymous = useIsAnonymous();

	return (
		<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.HELP}>
			{isAnonymous || !QueryFragment ? (
				<HelpButtonAnonymous />
			) : (
				<HelpButtonLoggedIn QueryFragment={QueryFragment} />
			)}
		</TopLevelErrorBoundary>
	);
}

export { HelpButton };
